import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  Print,
  Schedule,
  Assignment,
  Person,
  Today,
  TrendingUp,
  ExpandMore,
  Edit,
  CheckCircle,
  Cancel,
  Pending
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import ReportForm from './ReportForm';
import ReportView from './ReportView';
import DirectReportForm from './DirectReportForm';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const LaboratoristDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState({});
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [latestReports, setLatestReports] = useState([]);
  
  // Appointment state
  const [appointments, setAppointments] = useState([]);
  const [appointmentSearchTerm, setAppointmentSearchTerm] = useState('');
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState('');
  const [appointmentDateFilter, setAppointmentDateFilter] = useState(null);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Report state
  const [reports, setReports] = useState([]);
  const [reportSearchTerm, setReportSearchTerm] = useState('');
  const [reportStatusFilter, setReportStatusFilter] = useState('ALL');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showDirectReportDialog, setShowDirectReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 1) { // Appointments tab
      fetchAppointments();
    } else if (activeTab === 2) { // Reports tab
      fetchReports();
    }
  }, [activeTab, appointmentStatusFilter, appointmentDateFilter, reportStatusFilter]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsResponse, todayResponse, upcomingResponse, latestResponse] = await Promise.all([
        axios.get(`${API_URL}/api/laboratorist/dashboard/stats`),
        axios.get(`${API_URL}/api/laboratorist/dashboard/today-appointments`),
        axios.get(`${API_URL}/api/laboratorist/dashboard/upcoming-appointments`),
        axios.get(`${API_URL}/api/laboratorist/dashboard/latest-reports`)
      ]);
      
      setDashboardStats(statsResponse.data);
      setTodayAppointments(todayResponse.data);
      setUpcomingAppointments(upcomingResponse.data);
      setLatestReports(latestResponse.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const params = new URLSearchParams();
      if (appointmentSearchTerm) params.append('search', appointmentSearchTerm);
      if (appointmentStatusFilter) params.append('status', appointmentStatusFilter);
      if (appointmentDateFilter) params.append('date', appointmentDateFilter.toISOString().split('T')[0]);
      
      const response = await axios.get(`${API_URL}/api/laboratorist/appointments?${params}`);
      setAppointments(response.data);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  };

  const fetchReports = async () => {
    console.log('fetchReports called, user:', user);
    if (!user || !user.id) {
      console.log('No user or user.id, returning early');
      return;
    }
    try {
      let url;
      let params = new URLSearchParams();

      if (reportStatusFilter === 'PENDING') {
        url = `${API_URL}/api/laboratorist/reports/pending`;
        params.append('laboratoristId', user.id);
      } else {
        // Use the general reports endpoint which returns all reports
        url = `${API_URL}/api/laboratorist/reports`;
        if (reportSearchTerm) {
          params.append('search', reportSearchTerm);
        }
      }
      
      console.log('Making API call to:', url, 'with params:', params.toString());
      const response = await axios.get(url, { params });
      console.log('API response:', response.data);
      
      let fetchedReports = response.data;
      console.log('Initial fetchedReports:', fetchedReports);
      
      if (reportStatusFilter === 'COMPLETED') {
        fetchedReports = fetchedReports.filter(report => report.status === 'COMPLETED');
        console.log('After COMPLETED filter:', fetchedReports);
      }
      
      setReports(fetchedReports);
      console.log('Reports state set to:', fetchedReports);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  const handleAppointmentSearch = () => {
    fetchAppointments();
  };

  const handleReportSearch = () => {
    fetchReports();
  };

  const handleUpdateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API_URL}/api/laboratorist/appointments/${appointmentId}/status?status=${status}`);
      setSuccess('Appointment status updated successfully');
      fetchAppointments();
      fetchDashboardData();
    } catch (err) {
      setError('Failed to update appointment status');
      console.error('Error updating appointment status:', err);
    }
  };

  const handleViewAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDialog(true);
  };

  const handleCreateReportFromAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowReportDialog(true);
  };

  const handleCreateDirectReport = () => {
    setShowDirectReportDialog(true);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReportDialog(true);
  };

  const handleReportSaved = () => {
    setShowReportDialog(false);
    setShowDirectReportDialog(false);
    setSelectedAppointment(null);
    setSelectedReport(null);
    fetchDashboardData();
    if (activeTab === 2) {
      fetchReports();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'primary';
      case 'IN_PROGRESS': return 'warning';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SCHEDULED': return <Schedule />;
      case 'IN_PROGRESS': return <Pending />;
      case 'COMPLETED': return <CheckCircle />;
      case 'CANCELLED': return <Cancel />;
      default: return <Schedule />;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Dashboard Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Today color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{dashboardStats.todayAppointments || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Today's Appointments</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Pending color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{dashboardStats.pendingAppointments || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Pending Appointments</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{dashboardStats.todayReports || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Today's Reports</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{dashboardStats.totalReports || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Reports</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ width: '100%', borderRadius: 3, boxShadow: '0 4px 24px rgba(42,46,91,0.08)' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Dashboard" />
          <Tab label="Appointments" />
          <Tab label="Reports" />
        </Tabs>

        {/* Dashboard Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Today's Appointments */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2A2E5B', fontWeight: 700 }}>Today's Appointments</Typography>
                    {todayAppointments.length === 0 ? (
                      <Typography color="text.secondary">No appointments for today</Typography>
                    ) : (
                      todayAppointments.map((appointment) => (
                        <Accordion key={appointment.id} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Person sx={{ mr: 1 }} />
                              <Typography sx={{ flexGrow: 1 }}>
                                {appointment.patient.name}
                              </Typography>
                              <Chip 
                                label={appointment.status} 
                                color={getStatusColor(appointment.status)}
                                size="small"
                                sx={{ borderRadius: '16px', fontWeight: 600 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2">
                              <strong>Phone:</strong> {appointment.patient.phone}<br/>
                              <strong>Time:</strong> {new Date(appointment.appointmentTime).toLocaleString()}<br/>
                              <strong>Tests:</strong> {appointment.tests.map(t => t.name).join(', ')}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => handleViewAppointment(appointment)}
                                sx={{ mr: 1 }}
                              >
                                View Details
                              </Button>
                              <Button 
                                size="small" 
                                variant="contained" 
                                onClick={() => handleCreateReportFromAppointment(appointment)}
                              >
                                Create Report
                              </Button>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Latest Reports */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2A2E5B', fontWeight: 700 }}>Latest Reports</Typography>
                    {latestReports.length === 0 ? (
                      <Typography color="text.secondary">No recent reports</Typography>
                    ) : (
                      latestReports.map((report) => (
                        <Accordion key={report.id} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Assignment sx={{ mr: 1 }} />
                              <Typography sx={{ flexGrow: 1 }}>
                                {report.patient.name}
                              </Typography>
                              <Chip 
                                label={report.status} 
                                color={report.status === 'COMPLETED' ? 'success' : 'warning'}
                                size="small"
                                sx={{ borderRadius: '16px', fontWeight: 600 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2">
                              <strong>Phone:</strong> {report.patient.phone}<br/>
                              <strong>Date:</strong> {new Date(report.date).toLocaleDateString()}<br/>
                              <strong>Status:</strong> {report.status}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => handleViewReport(report)}
                                sx={{ mr: 1 }}
                              >
                                View Report
                              </Button>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Appointments Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#2A2E5B', fontWeight: 700 }}>Appointment Management</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateDirectReport}
                sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
              >
                Create Direct Report
              </Button>
            </Box>

            {/* Search and Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by patient name or phone"
                  value={appointmentSearchTerm}
                  onChange={(e) => setAppointmentSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAppointmentSearch()}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={appointmentStatusFilter}
                    label="Status"
                    onChange={(e) => setAppointmentStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Filter by date"
                    value={appointmentDateFilter}
                    onChange={setAppointmentDateFilter}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Search />}
                  onClick={handleAppointmentSearch}
                >
                  Search
                </Button>
              </Grid>
            </Grid>

            {/* Appointments Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Tests</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment) => (
                    <TableRow key={appointment.id} sx={{ background: (theme) => theme.palette.background.default }}>
                      <TableCell>{appointment.patient.name}</TableCell>
                      <TableCell>{appointment.patient.phone}</TableCell>
                      <TableCell>
                        {new Date(appointment.appointmentTime).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {appointment.tests.map(test => test.name).join(', ')}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={getStatusIcon(appointment.status)}
                          label={appointment.status} 
                          color={getStatusColor(appointment.status)}
                          size="small"
                          sx={{ borderRadius: '16px', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewAppointment(appointment)}
                        >
                          <Visibility />
                        </IconButton>
                        {appointment.status === 'SCHEDULED' && (
                          <>
                            <IconButton 
                              size="small" 
                              onClick={() => handleUpdateAppointmentStatus(appointment.id, 'IN_PROGRESS')}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleCreateReportFromAppointment(appointment)}
                            >
                              <Assignment />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Reports Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#2A2E5B', fontWeight: 700 }}>Report Management</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateDirectReport}
                sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
              >
                Create Direct Report
              </Button>
            </Box>

            {/* Search and Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search by patient name or phone"
                  value={reportSearchTerm}
                  onChange={(e) => setReportSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleReportSearch()}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={reportStatusFilter}
                    label="Status"
                    onChange={(e) => setReportStatusFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Search />}
                  onClick={handleReportSearch}
                >
                  Search
                </Button>
              </Grid>
            </Grid>

            {/* Reports Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id} sx={{ background: (theme) => theme.palette.background.default }}>
                      <TableCell>{report.patient.name}</TableCell>
                      <TableCell>{report.patient.phone}</TableCell>
                      <TableCell>
                        {new Date(report.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={report.status} 
                          color={report.status === 'COMPLETED' ? 'success' : 'warning'}
                          size="small"
                          sx={{ borderRadius: '16px', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewReport(report)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <Dialog 
        open={showAppointmentDialog} 
        onClose={() => setShowAppointmentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Appointment Details</DialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Box>
              <Typography variant="h6">Patient Information</Typography>
              <Typography><strong>Name:</strong> {selectedAppointment.patient.name}</Typography>
              <Typography><strong>Phone:</strong> {selectedAppointment.patient.phone}</Typography>
              <Typography><strong>Email:</strong> {selectedAppointment.patient.email}</Typography>
              <Typography><strong>Age:</strong> {selectedAppointment.patient.age} years</Typography>
              <Typography><strong>Gender:</strong> {selectedAppointment.patient.gender}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6">Appointment Information</Typography>
              <Typography><strong>Date & Time:</strong> {new Date(selectedAppointment.appointmentTime).toLocaleString()}</Typography>
              <Typography><strong>Status:</strong> {selectedAppointment.status}</Typography>
              <Typography><strong>Notes:</strong> {selectedAppointment.notes || 'No notes'}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6">Tests</Typography>
              {selectedAppointment.tests.map((test, index) => (
                <Typography key={index}>• {test.name}</Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAppointmentDialog(false)}>Close</Button>
          {selectedAppointment && selectedAppointment.status === 'SCHEDULED' && (
            <Button 
              variant="contained"
              onClick={() => handleCreateReportFromAppointment(selectedAppointment)}
            >
              Create Report
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog 
        open={showReportDialog} 
        onClose={() => setShowReportDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedReport ? 'Report Details' : 'Create Report from Appointment'}
        </DialogTitle>
        <DialogContent>
          {selectedReport ? (
            <ReportView report={selectedReport} />
          ) : selectedAppointment ? (
            <ReportForm 
              appointment={selectedAppointment}
              onSaved={handleReportSaved}
              onCancel={() => setShowReportDialog(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog 
        open={showDirectReportDialog} 
        onClose={() => setShowDirectReportDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Create Direct Report</DialogTitle>
        <DialogContent>
          <DirectReportForm 
            onSaved={handleReportSaved}
            onCancel={() => setShowDirectReportDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default LaboratoristDashboard; 